import type { DecisionAgentDomain } from '../decision-agent-platform';
import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

// origin/main literals: apps/api/src/decision-modules/org-analysis-optimization/
// org-analysis-optimization.data-lake-source.ts
// (ORG_ANALYSIS_OPTIMIZATION_SCENARIO_FORMULA_CONTEXT_KIND,
// ORG_ANALYSIS_OPTIMIZATION_DATA_LAKE_SCHEMA_VERSION). Confirmed live on tenant
// helix_retail 2026-08-06: 2 APPROVED, ingest-anchored connector-derived rows
// (latest approvedAt 2026-07-20, asOf 2026-07-02) carrying the bundled
// orgAnalysisScenarioRawSnapshot that `loadOrgAnalysisScenarioFormulaEvidence`
// resolves — a single artifact backs all thirteen scenario evidence classes below.
//
// SCOPE NOTE (deliberate, not an oversight): org-analysis-optimization's BASE
// evidence keys — org_state_as_is, span_layer_metrics, position_cost_roster,
// function_taxonomy, people_tenure, initiative_capacity_binding, severance_policy,
// workforce_legal_gate, org_baseline_snapshot, position_cost_baseline,
// org_cost_sub_lever_buildup — are read from dedicated governed tables (OrgState,
// OrgPositionCost, OrgPositionCapacity, CapabilityAssignment, PersonProfile,
// RifProgram, ...) via org-analysis-optimization.data-lake-source.ts, NOT from
// AskHelixMemoryArtifact. There is no contextKind to resolve for them through this
// evidence-contract substrate, so registering them here would invent a lookup the
// engine never performs. They stay CONTRACT_ABSENT under this instrument on
// purpose; only the scenario evidence-pack keys, which genuinely resolve through a
// governed AskHelixMemoryArtifact contextKind, are registered.
export const ORG_ANALYSIS_OPTIMIZATION_SCENARIO_EVIDENCE_KEYS = {
  optionDefinition: 'option_definition',
  optionActionPlan: 'option_action_plan',
  optionSchedule: 'option_schedule',
  optionCostCashFlow: 'option_cost_cash_flow',
  financeAnchor: 'finance_anchor',
  operatingOutcomeFact: 'operating_outcome_fact',
  activityWorkAssignment: 'activity_work_assignment',
  scenarioRiskModel: 'scenario_risk_model',
  workforceAttritionCohort: 'workforce_attrition_cohort',
  realizationActuals: 'realization_actuals',
  historicalExecutionActuals: 'historical_execution_actuals',
  optionDecisionPolicy: 'option_decision_policy',
  intentEvidenceManifest: 'intent_evidence_manifest',
} as const;

type OrgAnalysisOptimizationScenarioEvidenceKey =
  (typeof ORG_ANALYSIS_OPTIMIZATION_SCENARIO_EVIDENCE_KEYS)[keyof typeof ORG_ANALYSIS_OPTIMIZATION_SCENARIO_EVIDENCE_KEYS];

// Mirrors ORG_ANALYSIS_DISTINCT_SCENARIO_EVIDENCE_STATE
// (org-analysis-optimization.manifest.ts).
const ORG_ANALYSIS_OPTIMIZATION_DOMAIN_BY_EVIDENCE_KEY: Readonly<
  Record<OrgAnalysisOptimizationScenarioEvidenceKey, DecisionAgentDomain>
> = {
  option_definition: 'operations',
  option_action_plan: 'operations',
  option_schedule: 'operations',
  option_cost_cash_flow: 'finance',
  finance_anchor: 'finance',
  operating_outcome_fact: 'operations',
  activity_work_assignment: 'people',
  scenario_risk_model: 'operations',
  workforce_attrition_cohort: 'people',
  realization_actuals: 'finance',
  historical_execution_actuals: 'operations',
  option_decision_policy: 'operations',
  intent_evidence_manifest: 'operations',
};

export const ORG_ANALYSIS_OPTIMIZATION_CONTEXT_KIND = 'decision_agent:org_analysis_optimization';
export const ORG_ANALYSIS_OPTIMIZATION_SCHEMA_VERSION =
  'org_analysis_optimization_governed_snapshot_v1';

const NO_ORG_ANALYSIS_OPTIMIZATION_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

function orgAnalysisOptimizationScenarioEntry(evidenceKey: OrgAnalysisOptimizationScenarioEvidenceKey) {
  return defineModuleEvidenceEntry(evidenceKey, {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
    contextKind: ORG_ANALYSIS_OPTIMIZATION_CONTEXT_KIND,
    schemaVersion: ORG_ANALYSIS_OPTIMIZATION_SCHEMA_VERSION,
    bindingPlaceholders: NO_ORG_ANALYSIS_OPTIMIZATION_BINDING_PLACEHOLDERS,
    domain: ORG_ANALYSIS_OPTIMIZATION_DOMAIN_BY_EVIDENCE_KEY[evidenceKey],
    freshnessCadence: 'weekly',
    requiredFor: ['scenario', 'decision_pack', 'ask_helix'],
    missingBehavior: 'block',
    description:
      `Approved org-analysis-optimization scenario evidence-pack bundle published by the governed ` +
      `data-lake source; backs the ${evidenceKey} member of the bundled orgAnalysisScenarioRawSnapshot ` +
      'artifact that loadOrgAnalysisScenarioFormulaEvidence resolves.',
  });
}

export const ORG_ANALYSIS_OPTIMIZATION_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'org-analysis-optimization',
  {
    option_definition: orgAnalysisOptimizationScenarioEntry('option_definition'),
    option_action_plan: orgAnalysisOptimizationScenarioEntry('option_action_plan'),
    option_schedule: orgAnalysisOptimizationScenarioEntry('option_schedule'),
    option_cost_cash_flow: orgAnalysisOptimizationScenarioEntry('option_cost_cash_flow'),
    finance_anchor: orgAnalysisOptimizationScenarioEntry('finance_anchor'),
    operating_outcome_fact: orgAnalysisOptimizationScenarioEntry('operating_outcome_fact'),
    activity_work_assignment: orgAnalysisOptimizationScenarioEntry('activity_work_assignment'),
    scenario_risk_model: orgAnalysisOptimizationScenarioEntry('scenario_risk_model'),
    workforce_attrition_cohort: orgAnalysisOptimizationScenarioEntry('workforce_attrition_cohort'),
    realization_actuals: orgAnalysisOptimizationScenarioEntry('realization_actuals'),
    historical_execution_actuals: orgAnalysisOptimizationScenarioEntry('historical_execution_actuals'),
    option_decision_policy: orgAnalysisOptimizationScenarioEntry('option_decision_policy'),
    intent_evidence_manifest: orgAnalysisOptimizationScenarioEntry('intent_evidence_manifest'),
  },
  {
    evidenceKeys: Object.values(ORG_ANALYSIS_OPTIMIZATION_SCENARIO_EVIDENCE_KEYS),
    sourceClasses: [DECISION_AGENT_SOURCE_CLASSES.connectorDerived],
    contextKinds: [ORG_ANALYSIS_OPTIMIZATION_CONTEXT_KIND],
    schemaVersions: [ORG_ANALYSIS_OPTIMIZATION_SCHEMA_VERSION],
    stateKeys: Object.values(ORG_ANALYSIS_OPTIMIZATION_SCENARIO_EVIDENCE_KEYS),
    bindingPlaceholders: NO_ORG_ANALYSIS_OPTIMIZATION_BINDING_PLACEHOLDERS,
  },
);

export type OrgAnalysisOptimizationModuleEvidenceContract =
  typeof ORG_ANALYSIS_OPTIMIZATION_MODULE_EVIDENCE_CONTRACT;
